import React, { useEffect, useRef, useState } from "react";
import { api, formatApiError, brandAssetUrl } from "@/lib/api";
import { toast } from "sonner";
import { useSiteConfig } from "@/contexts/SiteConfigContext";
import { Trash2, Upload } from "lucide-react";

function AssetSlot({ kind, label, hint, has, version, onChanged }) {
  const inputRef = useRef(null);
  const [busy, setBusy] = useState(false);

  const upload = async (file) => {
    if (!file) return;
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      await api.post(`/admin/branding/${kind}`, fd);
      toast.success(`${label} uploaded`);
      onChanged();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(false);
      if (inputRef.current) inputRef.current.value = "";
    }
  };

  const remove = async () => {
    setBusy(true);
    try {
      await api.delete(`/admin/branding/${kind}`);
      toast.success(`${label} removed`);
      onChanged();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="border border-white/10 p-5 space-y-3" data-testid={`brand-asset-${kind}`}>
      <p className="text-xs uppercase tracking-[0.3em] text-white/40">{label}</p>
      <div className="h-24 flex items-center justify-center bg-white/5">
        {has ? (
          <img
            src={brandAssetUrl(`/api/public/branding/${kind}`, version)}
            alt={label}
            className="max-h-20 max-w-full object-contain"
            data-testid={`brand-asset-${kind}-preview`}
          />
        ) : (
          <span className="text-white/30 text-xs font-condensed uppercase tracking-widest">Not set</span>
        )}
      </div>
      <p className="text-xs text-white/40 font-condensed">{hint}</p>
      <div className="flex gap-3">
        <input
          ref={inputRef}
          type="file"
          accept="image/*"
          className="hidden"
          onChange={(e) => upload(e.target.files?.[0])}
        />
        <button
          type="button"
          disabled={busy}
          onClick={() => inputRef.current?.click()}
          className="flex items-center gap-2 border border-white/25 px-4 py-2 text-xs uppercase tracking-[0.25em] hover:border-[var(--pm-orange)] disabled:opacity-50"
          data-testid={`brand-asset-${kind}-upload`}
        >
          <Upload size={13} /> {busy ? "Working…" : has ? "Replace" : "Upload"}
        </button>
        {has && (
          <button
            type="button"
            disabled={busy}
            onClick={remove}
            className="flex items-center gap-2 border border-red-500/40 text-red-400 px-4 py-2 text-xs uppercase tracking-[0.25em] hover:border-red-400 disabled:opacity-50"
            data-testid={`brand-asset-${kind}-remove`}
          >
            <Trash2 size={13} /> Remove
          </button>
        )}
      </div>
    </div>
  );
}

export default function BrandAssetsCard() {
  const { refresh } = useSiteConfig();
  const [cfg, setCfg] = useState(null);
  const [heroes, setHeroes] = useState([]);
  const [busy, setBusy] = useState(false);
  const heroInputRef = useRef(null);

  const load = () => {
    api.get("/public/site-config").then((r) => setCfg(r.data)).catch(() => {});
    api.get("/admin/branding-hero").then((r) => setHeroes(r.data.items || [])).catch(() => {});
  };
  // eslint-disable-next-line react-hooks/exhaustive-deps -- fetch once on mount
  useEffect(() => { load(); }, []);

  const changed = () => { load(); refresh(); };

  const uploadHeroes = async (files) => {
    const list = Array.from(files || []);
    if (!list.length) return;
    setBusy(true);
    let ok = 0;
    for (const f of list) {
      try {
        const fd = new FormData();
        fd.append("file", f);
        await api.post("/admin/branding-hero", fd);
        ok += 1;
      } catch (e) {
        toast.error(`${f.name}: ${formatApiError(e)}`);
        break;
      }
    }
    if (ok) toast.success(`${ok} hero image${ok > 1 ? "s" : ""} uploaded`);
    setBusy(false);
    if (heroInputRef.current) heroInputRef.current.value = "";
    changed();
  };

  const removeHero = async (hid) => {
    try {
      await api.delete(`/admin/branding-hero/${hid}`);
      toast.success("Hero image removed");
      changed();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  if (!cfg) {
    return <div className="border-t border-white/10 pt-10"><p className="text-white/50 text-sm">Loading brand assets…</p></div>;
  }

  const usingCustomHero = (cfg.custom_hero_count || 0) > 0;

  return (
    <div className="border-t border-white/10 pt-10 space-y-6" data-testid="brand-assets">
      <div>
        <h3 className="font-serif text-2xl">Brand assets</h3>
        <p className="text-sm text-white/50 mt-1 max-w-2xl">
          Per-vertical imagery: the nav/header logo, the watermark corner icon stamped onto new uploads,
          and the rotating homepage hero photos.
        </p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <AssetSlot
          kind="nav_logo"
          label="Nav / header logo"
          hint="Shown in the top navigation and homepage brand panel. Transparent PNG recommended, landscape or square."
          has={cfg.has_nav_logo}
          version={cfg.branding_version}
          onChanged={changed}
        />
        <AssetSlot
          kind="watermark_icon"
          label="Watermark corner icon"
          hint="Stamped next to the domain on every NEW photo preview. Existing photos keep their original watermark."
          has={cfg.has_watermark_icon}
          version={cfg.branding_version}
          onChanged={changed}
        />
      </div>

      <div className="space-y-3" data-testid="hero-images-section">
        <div className="flex items-end justify-between flex-wrap gap-3">
          <div>
            <p className="text-xs uppercase tracking-[0.3em] text-white/40">Homepage hero photos</p>
            <p className="text-xs text-white/40 font-condensed mt-1">
              {usingCustomHero
                ? `Using ${cfg.custom_hero_count} custom image${cfg.custom_hero_count > 1 ? "s" : ""} (max 12).`
                : `Using the "${cfg.preset_key}" preset defaults. Upload your own to override.`}
            </p>
          </div>
          <div>
            <input
              ref={heroInputRef}
              type="file"
              accept="image/*"
              multiple
              className="hidden"
              onChange={(e) => uploadHeroes(e.target.files)}
            />
            <button
              type="button"
              disabled={busy}
              onClick={() => heroInputRef.current?.click()}
              className="flex items-center gap-2 border border-white/25 px-4 py-2 text-xs uppercase tracking-[0.25em] hover:border-[var(--pm-orange)] disabled:opacity-50"
              data-testid="hero-images-upload"
            >
              <Upload size={13} /> {busy ? "Uploading…" : "Upload hero photos"}
            </button>
          </div>
        </div>

        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-2">
          {(cfg.hero_images || []).map((src, i) => {
            const custom = usingCustomHero ? heroes[i] : null;
            return (
              <div key={src} className="relative group aspect-[3/2] bg-white/5 overflow-hidden">
                <img src={brandAssetUrl(src, cfg.branding_version)} alt="" className="w-full h-full object-cover" />
                {custom && (
                  <button
                    type="button"
                    onClick={() => removeHero(custom.id)}
                    className="absolute top-1 right-1 bg-black/70 text-red-400 p-1.5 opacity-0 group-hover:opacity-100 transition-opacity"
                    title="Remove"
                    data-testid={`hero-image-remove-${custom.id}`}
                  >
                    <Trash2 size={13} />
                  </button>
                )}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
